import { itemsApi } from "#/api/itemsApi";
import { itemTemplateApi } from "#/api/itemTemplateApi";
import type { RfidTag } from "#/types/rfidTag";
import { getRandomColor } from "#/utils/ColorUtils";
import { Badge, Button, Modal, Select, Text } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { usePermission } from "#/hooks/usePermission";
import PermissionTooltip from "#/components/ui/PermissionTooltip";

interface BulkCreateItemsModalProps {
  opened: boolean;
  onClose: () => void;
  selectedTags: RfidTag[];
  onSuccess: () => void;
}

const BulkCreateItemsModal = ({
  opened,
  onClose,
  selectedTags,
  onSuccess,
}: BulkCreateItemsModalProps) => {
  const { t } = useTranslation();
  const { canWrite } = usePermission();
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(
    null
  );

  const { data: { templates = [] } = {}, isLoading: templatesLoading } =
    useQuery({
      queryKey: ["templates-for-bulk-create"],
      queryFn: async () => {
        const response = await itemTemplateApi.getAll({
          page: 1,
          limit: 10000,
        });
        if (!response.data) {
          throw new Error("Failed to fetch templates");
        }
        return {
          templates: response.data.data,
        };
      },
      enabled: opened,
      staleTime: 1000 * 60,
    });

  const bulkCreateMutation = useMutation({
    mutationFn: async ({
      templateId,
      rfidIds,
    }: {
      templateId: number;
      rfidIds: number[];
    }) => {
      const response = await itemsApi.bulkCreateFromTemplate(
        templateId,
        rfidIds
      );
      if (!response.data) {
        throw new Error("Failed to create items");
      }
      return response.data;
    },
    onSuccess: (items) => {
      notifications.show({
        title: t("common.success"),
        message: t("rfidTags.bulkCreate.success", { count: items.length }),
        color: "green",
      });
      handleClose();
      onSuccess();
    },
    onError: (error: unknown) => {
      const errorMessage =
        (error as { response?: { data?: { message?: string } } })?.response
          ?.data?.message || t("rfidTags.bulkCreate.error");
      notifications.show({
        title: t("common.error"),
        message: errorMessage,
        color: "red",
      });
    },
  });

  const handleClose = () => {
    setSelectedTemplateId(null);
    onClose();
  };

  const handleCreate = () => {
    if (selectedTemplateId) {
      bulkCreateMutation.mutate({
        templateId: parseInt(selectedTemplateId),
        rfidIds: selectedTags.map((tag) => tag.id),
      });
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      centered
      title={t("rfidTags.bulkCreate.title")}
      closeOnClickOutside={false}
      size="xl"
      classNames={{
        title: "font-bold",
      }}
    >
      <div className="w-full flex flex-col gap-4">
        <Text size="sm" c="dimmed">
          {t("rfidTags.bulkCreate.description", { count: selectedTags.length })}
        </Text>

        {templatesLoading ? (
          <Text size="sm" c="dimmed">
            {t("rfidTags.bulkCreate.loadingTemplates")}
          </Text>
        ) : templates.length === 0 ? (
          <Text size="sm" c="dimmed">
            {t("rfidTags.bulkCreate.noTemplatesFound")}
          </Text>
        ) : (
          <Select
            label={t("rfidTags.bulkCreate.templateLabel")}
            placeholder={t("rfidTags.bulkCreate.templatePlaceholder")}
            searchable
            withAsterisk
            data={templates.map((template) => ({
              label: template.name,
              value: template.id.toString(),
            }))}
            value={selectedTemplateId}
            onChange={setSelectedTemplateId}
            description={
              selectedTemplateId
                ? templates.find((t) => t.id.toString() === selectedTemplateId)
                    ?.description || t("rfidTags.bulkCreate.noDescription")
                : t("rfidTags.bulkCreate.chooseTemplate")
            }
          />
        )}

        <div className="w-full p-3 bg-gray-50 rounded-md border border-gray-200 flex flex-col gap-2">
          <span className="text-xs font-bold text-slate-600 ">
            {t("rfidTags.bulkCreate.selectedTags", { count: selectedTags.length })}
          </span>
          <div className="flex flex-wrap gap-2">
            {selectedTags.map((tag) => (
              <Badge
                key={tag.id}
                color={getRandomColor(tag.rfid)}
                variant="dot"
                size="md"
                className="font-mono"
              >
                {tag.rfid}
              </Badge>
            ))}
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 pt-2">
          <Button
            variant="outline"
            color="black"
            onClick={handleClose}
            disabled={bulkCreateMutation.isPending}
          >
            {t("common.cancel")}
          </Button>
          <PermissionTooltip disabled={!canWrite()}>
            <Button
              color="black"
              disabled={
                !selectedTemplateId ||
                templates.length === 0 ||
                bulkCreateMutation.isPending ||
                !canWrite()
              }
              onClick={handleCreate}
              loading={bulkCreateMutation.isPending}
            >
              {t("rfidTags.bulkCreate.createButton", { count: selectedTags.length })}
            </Button>
          </PermissionTooltip>
        </div>
      </div>
    </Modal>
  );
};

export default BulkCreateItemsModal;
